import React, { useEffect, useState, useCallback } from "react";
import { Save, Truck, AlertCircle, Plug, CheckCircle2, Info, Eye, EyeOff, ShieldCheck, ShieldX } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, isAuthError } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";

const DEFAULT_COURIER_MAP = {
  jne: "JNE",
  jnt: "JNT",
  sicepat: "SICEPAT",
  anteraja: "ANTERAJA",
  gosend: "GOSEND",
  grab: "GRAB",
};

const BASE_URL_PROD = "https://api-shipment.jubelio.com";
const BASE_URL_SANDBOX = "https://api-shipment.sandbox.jubelio.com";

function OAuthBadge({ status }) {
  // status: idle | connected | disconnected
  if (status === "connected") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300" data-testid="oauth-badge-connected">
        <ShieldCheck size={12} /> OAuth Connected
      </span>
    );
  }
  if (status === "disconnected") {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-red-100 text-red-800 border border-red-300" data-testid="oauth-badge-disconnected">
        <ShieldX size={12} /> OAuth Not Connected
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-gray-100 text-gray-700 border border-gray-300" data-testid="oauth-badge-idle">
      <Plug size={12} /> Belum dites
    </span>
  );
}

export default function JubelioSettings() {
  const [cfg, setCfg] = useState({
    enabled: false,
    sandbox: true,
    use_real_sandbox: false,
    client_id: "",
    client_secret: "",
    base_url: BASE_URL_PROD,
    warehouse_id: "",
    sender_name: "",
    sender_phone: "",
    sender_address: "",
    sender_city: "",
    sender_postal_code: "",
    enable_create_awb: false,
    courier_mapping: { ...DEFAULT_COURIER_MAP },
  });
  const [clientSecretMasked, setClientSecretMasked] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [active, setActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  // OAuth connection status: idle | connected | disconnected (dari hasil Test Koneksi terakhir)
  const [oauthStatus, setOauthStatus] = useState("idle");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/settings/jubelio");
      if (data && Object.keys(data).length) {
        setCfg((prev) => {
          const next = { ...prev, ...data };
          next.courier_mapping = {
            ...DEFAULT_COURIER_MAP,
            ...(data.courier_mapping || {}),
          };
          if (!next.base_url || next.base_url.includes("api.jubelio.com")) {
            next.base_url = next.sandbox && next.use_real_sandbox ? BASE_URL_SANDBOX : BASE_URL_PROD;
          }
          return next;
        });
        setClientSecretMasked(!!data._client_secret_set);
        setActive(!!data._active);
      }
    } catch (e) {
      if (!isAuthError(e)) toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setLoading(true);
    try {
      const payload = { ...cfg };
      // Jangan kirim client_secret kalau masih bentuk mask
      if (clientSecretMasked && payload.client_secret && payload.client_secret.includes("***")) {
        delete payload.client_secret;
      }
      await api.put("/admin/settings/jubelio", payload);
      toast.success("Setting Jubelio berhasil disimpan");
      setTestResult(null);
      await load();
    } catch (e) {
      if (!isAuthError(e)) toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/admin/settings/jubelio/test-connection");
      setTestResult(data);
      setOauthStatus(data.ok ? "connected" : "disconnected");
      if (data.ok) toast.success(data.message || "Koneksi berhasil");
      else toast.error(data.message || "Koneksi gagal");
    } catch (e) {
      if (isAuthError(e)) return;
      const msg = formatApiError(e);
      setTestResult({ ok: false, message: msg });
      setOauthStatus("disconnected");
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  const updateCourier = (key, value) => {
    setCfg((c) => ({ ...c, courier_mapping: { ...c.courier_mapping, [key]: value } }));
  };

  // Auto-suggest base_url saat user toggle sandbox/production
  const onSandboxToggle = (v) => {
    setCfg((c) => {
      const usingDefault = !c.base_url || [BASE_URL_PROD, BASE_URL_SANDBOX].includes(c.base_url);
      return {
        ...c,
        sandbox: v,
        base_url: usingDefault ? (v && c.use_real_sandbox ? BASE_URL_SANDBOX : BASE_URL_PROD) : c.base_url,
      };
    });
  };

  const onRealSandboxToggle = (v) => {
    setCfg((c) => {
      const usingDefault = !c.base_url || [BASE_URL_PROD, BASE_URL_SANDBOX].includes(c.base_url);
      return {
        ...c,
        use_real_sandbox: v,
        base_url: usingDefault ? (v ? BASE_URL_SANDBOX : BASE_URL_PROD) : c.base_url,
      };
    });
  };

  const mockMode = cfg.sandbox && !cfg.use_real_sandbox;

  return (
    <AdminLayout title="Jubelio Shipment">
      <div className="max-w-3xl space-y-6" data-testid="admin-jubelio-settings">
        <Card className={`p-6 border flex gap-3 ${active ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
          {active ? <CheckCircle2 className="text-emerald-600 shrink-0 mt-0.5" size={20} /> : <AlertCircle className="text-amber-600 shrink-0 mt-0.5" size={20} />}
          <div className={`text-sm ${active ? "text-emerald-900" : "text-amber-900"}`} data-testid="jubelio-status-banner">
            {active ? (
              <>
                <strong>Status: AKTIF{mockMode ? " (Sandbox / Mock)" : cfg.sandbox ? " (Sandbox Real)" : " (Production)"}.</strong>{" "}
                {mockMode
                  ? "Mock mode: ongkir & shipment menggunakan data simulasi tanpa hit API Jubelio."
                  : cfg.sandbox
                  ? "Sandbox real: aplikasi memanggil server sandbox Jubelio untuk validasi credential."
                  : "Production: aplikasi memanggil API Jubelio sungguhan. Pastikan credential & warehouse benar."}
              </>
            ) : (
              <>
                <strong>Status: Belum aktif.</strong> Checkout customer tetap memakai metode pengiriman lama (Gosend/JNE manual). Isi Client ID & Client Secret dari dashboard Jubelio → Setting → Developer, lalu aktifkan toggle.
              </>
            )}
          </div>
        </Card>

        <Card className="p-4 bg-blue-50 border border-blue-200 flex gap-3 text-sm text-blue-900">
          <Info size={18} className="shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <strong>Autentikasi: OAuth Client Credentials</strong>
              <OAuthBadge status={oauthStatus} />
            </div>
            <div className="mt-1">
              Sistem akan otomatis menukar <code>client_id</code> + <code>client_secret</code> menjadi bearer token melalui endpoint <code>{cfg.base_url || BASE_URL_PROD}/auth/generate-token</code>, lalu cache token di memory dengan refresh otomatis sebelum expired.
            </div>
          </div>
        </Card>

        <Card className="p-6 space-y-5">
          <div className="flex items-center gap-3">
            <Truck className="text-[#4A2E1F]" size={24} />
            <h2 className="text-lg font-semibold">Konfigurasi Jubelio Shipment</h2>
          </div>

          <div className="flex items-center justify-between p-4 bg-[#FAF9F6] rounded-xl border border-[#E5E5E5]">
            <div>
              <div className="font-semibold text-sm">Aktifkan Jubelio Shipment</div>
              <div className="text-xs text-[#7A5440] mt-0.5">Saat aktif, ongkir & pengiriman dapat menggunakan API Jubelio.</div>
            </div>
            <Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="jubelio-enable-toggle" />
          </div>

          <div className="flex items-center justify-between p-4 bg-[#FAF9F6] rounded-xl border border-[#E5E5E5]">
            <div>
              <div className="font-semibold text-sm">Mode Sandbox</div>
              <div className="text-xs text-[#7A5440] mt-0.5">
                ON saat testing, OFF saat production.
                {cfg.sandbox && (
                  <span className="block mt-0.5">
                    Sub-mode: {cfg.use_real_sandbox ? "Sandbox Real (hit server sandbox Jubelio)" : "Mock (data simulasi, tanpa credential)"}.
                  </span>
                )}
              </div>
            </div>
            <Switch checked={!!cfg.sandbox} onCheckedChange={onSandboxToggle} data-testid="jubelio-sandbox-toggle" />
          </div>

          {cfg.sandbox && (
            <div className="flex items-center justify-between p-4 bg-[#FAF9F6] rounded-xl border border-[#E5E5E5] ml-4">
              <div>
                <div className="font-semibold text-sm">Pakai Server Sandbox Real</div>
                <div className="text-xs text-[#7A5440] mt-0.5">
                  ON = panggil <code>api-shipment.sandbox.jubelio.com</code> dengan credential.<br />
                  OFF = pakai data mock di server kami (untuk demo UI).
                </div>
              </div>
              <Switch checked={!!cfg.use_real_sandbox} onCheckedChange={onRealSandboxToggle} data-testid="jubelio-real-sandbox-toggle" />
            </div>
          )}

          {/* Generate AWB Production gate — Fase 3 v1.8.
              Saat OFF → admin masih bisa Dry Run (preview payload), tapi
              real POST /shipments/create akan di-reject (HTTP 403).
              Saat ON → tombol Generate AWB di Admin Order Detail bisa
              dipakai. Indikator status di banner atas card. */}
          <div className="flex items-center justify-between p-4 rounded-xl border-2 transition-colors"
            style={{
              background: cfg.enable_create_awb ? "rgba(16,185,129,0.08)" : "rgba(245,158,11,0.08)",
              borderColor: cfg.enable_create_awb ? "rgb(16,185,129)" : "rgb(245,158,11)",
            }}
            data-testid="enable-create-awb-row"
          >
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <div className="font-semibold text-sm">Enable Generate AWB Production</div>
                {cfg.enable_create_awb ? (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300"
                    data-testid="awb-status-production"
                  >
                    🟢 Production Enabled
                  </span>
                ) : (
                  <span
                    className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300"
                    data-testid="awb-status-dryrun"
                  >
                    🟡 Dry Run Only
                  </span>
                )}
              </div>
              <div className="text-xs text-[#7A5440] mt-1">
                {cfg.enable_create_awb ? (
                  <>
                    <b>ON:</b> Tombol &quot;Generate AWB Production&quot; di Admin Order
                    Detail aktif — POST <code>/shipments/create</code> ke Jubelio
                    akan dijalankan sungguhan & resi (AWB) dibuat. Dry Run tetap
                    tersedia kapan saja.
                  </>
                ) : (
                  <>
                    <b>OFF (Default — aman):</b> Hanya <b>Dry Run</b> (preview
                    payload) yang diizinkan. Real call ke Jubelio akan ditolak
                    HTTP 403. Aktifkan toggle ini sebelum membuat AWB nyata.
                  </>
                )}
              </div>
            </div>
            <Switch
              checked={!!cfg.enable_create_awb}
              onCheckedChange={(v) => setCfg({ ...cfg, enable_create_awb: v })}
              data-testid="enable-create-awb-toggle"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Client ID</Label>
              <Input
                value={cfg.client_id || ""}
                onChange={(e) => setCfg({ ...cfg, client_id: e.target.value })}
                placeholder="Dari Jubelio Setting → Developer → API Keys"
                data-testid="jubelio-client-id"
              />
            </div>
            <div>
              <Label>Client Secret</Label>
              <div className="relative">
                <Input
                  type={showSecret ? "text" : "password"}
                  value={cfg.client_secret || ""}
                  onChange={(e) => { setCfg({ ...cfg, client_secret: e.target.value }); setClientSecretMasked(false); }}
                  placeholder={clientSecretMasked ? "(tersimpan — kosongkan untuk ubah)" : "Paste Client Secret"}
                  data-testid="jubelio-client-secret"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#7A5440] hover:text-[#4A2E1F] focus:outline-none"
                  title={showSecret ? "Sembunyikan" : "Tampilkan"}
                  data-testid="jubelio-secret-toggle"
                  aria-label={showSecret ? "Sembunyikan Client Secret" : "Tampilkan Client Secret"}
                >
                  {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {clientSecretMasked && <div className="text-xs text-[#5A8F3D] mt-1">Client Secret tersimpan. Ketik baru untuk ganti.</div>}
            </div>
            <div className="md:col-span-2">
              <Label>Base URL API</Label>
              <Input
                value={cfg.base_url || ""}
                onChange={(e) => setCfg({ ...cfg, base_url: e.target.value })}
                placeholder={BASE_URL_PROD}
                data-testid="jubelio-base-url"
              />
              <div className="text-xs text-[#7A5440] mt-1">
                Production: <code>{BASE_URL_PROD}</code> · Sandbox: <code>{BASE_URL_SANDBOX}</code>
              </div>
            </div>
            <div className="md:col-span-2">
              <Label>Warehouse / Origin ID</Label>
              <Input
                value={cfg.warehouse_id || ""}
                onChange={(e) => setCfg({ ...cfg, warehouse_id: e.target.value })}
                placeholder="Dari Jubelio: Inventory → Warehouses"
                data-testid="jubelio-warehouse-id"
              />
            </div>
          </div>

          <div className="pt-2 border-t border-[#F0E5C0]">
            <h3 className="font-semibold text-sm mb-3">Detail Pengirim (Origin)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input placeholder="Nama Pengirim" value={cfg.sender_name || ""} onChange={(e) => setCfg({ ...cfg, sender_name: e.target.value })} data-testid="jubelio-sender-name" />
              <Input placeholder="No HP Pengirim" value={cfg.sender_phone || ""} onChange={(e) => setCfg({ ...cfg, sender_phone: e.target.value })} data-testid="jubelio-sender-phone" />
              <Input className="md:col-span-2" placeholder="Alamat Lengkap" value={cfg.sender_address || ""} onChange={(e) => setCfg({ ...cfg, sender_address: e.target.value })} data-testid="jubelio-sender-address" />
              <Input placeholder="Kota" value={cfg.sender_city || ""} onChange={(e) => setCfg({ ...cfg, sender_city: e.target.value })} data-testid="jubelio-sender-city" />
              <Input placeholder="Kode Pos" value={cfg.sender_postal_code || ""} onChange={(e) => setCfg({ ...cfg, sender_postal_code: e.target.value })} data-testid="jubelio-sender-postal" />
            </div>
          </div>

          <div className="pt-2 border-t border-[#F0E5C0]">
            <h3 className="font-semibold text-sm mb-1">Courier Mapping</h3>
            <p className="text-xs text-[#7A5440] mb-3">Map kode shipping_method internal toko ke kode courier yang Jubelio kenali.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.keys(DEFAULT_COURIER_MAP).map((k) => (
                <div key={k} className="flex gap-2 items-center">
                  <div className="w-28 text-xs uppercase font-bold tracking-wider text-[#5C5C5C]">{k}</div>
                  <Input
                    value={cfg.courier_mapping?.[k] || ""}
                    onChange={(e) => updateCourier(k, e.target.value)}
                    placeholder={DEFAULT_COURIER_MAP[k]}
                    data-testid={`jubelio-courier-${k}`}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="pt-2 flex flex-wrap gap-2 items-center">
            <Button onClick={save} disabled={loading} data-testid="jubelio-save-btn" className="bg-[#4A2E1F] text-white">
              <Save size={16} className="mr-2" /> {loading ? "Menyimpan..." : "Simpan Setting"}
            </Button>
            <Button onClick={testConnection} disabled={testing || !cfg.enabled} variant="outline" data-testid="jubelio-test-btn">
              <Plug size={16} className="mr-2" /> {testing ? "Mengetes..." : "Test Koneksi"}
            </Button>
            {testResult && (
              <div className={`text-xs px-2 py-1 rounded ${testResult.ok ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`} data-testid="jubelio-test-result">
                {testResult.ok ? "✓ " : "✗ "}{testResult.message}{testResult.mode ? ` · mode=${testResult.mode}` : ""}
              </div>
            )}
          </div>
        </Card>

        {/* ───────── Diagnose Rates (Sprint diagnose GoSend/Grab) ───────── */}
        <DiagnoseRatesCard />

        {/* ───────── Phase 6 — Webhook Receiver Settings ───────── */}
        <WebhookSettingsCard />
      </div>
    </AdminLayout>
  );
}


function DiagnoseRatesCard() {
  const [form, setForm] = useState({
    origin_area_id: "",
    destination_area_id: "",
    origin_coordinate: "",
    destination_coordinate: "",
    weight: 1000,
    service_category_id: "",
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [filter, setFilter] = useState("");

  // Auto-prefill origin dari settings warehouse_origin saat mount
  useEffect(() => {
    api.get("/settings").then((r) => {
      const wh = r.data?.warehouse_origin || {};
      setForm((f) => ({
        ...f,
        origin_area_id: wh.area_id || f.origin_area_id,
        origin_coordinate: wh.coordinate || f.origin_coordinate,
      }));
    }).catch(() => {});
  }, []);

  const run = async () => {
    if (!form.origin_area_id || !form.destination_area_id) {
      toast.error("origin_area_id & destination_area_id wajib");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/jubelio/diagnose-rates", form);
      setResult(data);
      if (data.has_instant_couriers) {
        toast.success(`Ditemukan ${data.instant_couriers.length} kurir instant`);
      } else {
        toast.warning("GoSend/Grab TIDAK ditemukan di response Jubelio");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  // Apply filter to normalized + couriers list
  const filtered = React.useMemo(() => {
    if (!result?.normalized_sample || !filter) return result?.normalized_sample || [];
    const f = filter.toLowerCase();
    return result.normalized_sample.filter((r) => {
      const blob = [r.courier_name, r.courier_service_name, r.service, r.courier].join(" ").toLowerCase();
      return blob.includes(f);
    });
  }, [result, filter]);

  return (
    <Card className="p-6 space-y-4 bg-white" data-testid="diagnose-rates-card">
      <div className="flex items-center gap-2">
        <AlertCircle size={20} className="text-[#E85D04]" />
        <h2 className="font-display text-xl font-bold">Diagnose Rates</h2>
      </div>
      <p className="text-xs text-[#7A5440]">
        Uji panggilan <code>/rates</code> ke Jubelio tanpa hit checkout.
        Berguna untuk debug GoSend/Grab/instant yang tidak muncul.
        <b>Koordinat wajib untuk kurir instant.</b>
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>Origin Area ID *</Label>
          <Input
            value={form.origin_area_id}
            onChange={(e) => setForm({ ...form, origin_area_id: e.target.value })}
            placeholder="31710101"
            data-testid="diag-origin-area-id"
          />
        </div>
        <div>
          <Label>Destination Area ID *</Label>
          <Input
            value={form.destination_area_id}
            onChange={(e) => setForm({ ...form, destination_area_id: e.target.value })}
            placeholder="35780501"
            data-testid="diag-dest-area-id"
          />
        </div>
        <div>
          <Label>Origin Coordinate <span className="text-[10px] text-amber-700">(wajib untuk GoSend/Grab)</span></Label>
          <Input
            value={form.origin_coordinate}
            onChange={(e) => setForm({ ...form, origin_coordinate: e.target.value })}
            placeholder="(-6.19,106.84)"
            data-testid="diag-origin-coord"
          />
        </div>
        <div>
          <Label>Destination Coordinate <span className="text-[10px] text-amber-700">(wajib untuk GoSend/Grab)</span></Label>
          <Input
            value={form.destination_coordinate}
            onChange={(e) => setForm({ ...form, destination_coordinate: e.target.value })}
            placeholder="(-7.25,112.75)"
            data-testid="diag-dest-coord"
          />
        </div>
        <div>
          <Label>Weight (gram)</Label>
          <Input
            type="number"
            value={form.weight}
            onChange={(e) => setForm({ ...form, weight: Number(e.target.value) || 1000 })}
            data-testid="diag-weight"
          />
        </div>
        <div>
          <Label>Service Category ID <span className="text-[10px] text-[#7A5440]">(kosong = /rates/all)</span></Label>
          <Input
            value={form.service_category_id}
            onChange={(e) => setForm({ ...form, service_category_id: e.target.value })}
            placeholder="1 = Reguler, 7 = Instant"
            data-testid="diag-service-cat"
          />
        </div>
      </div>

      <Button onClick={run} disabled={busy} className="brand-btn-primary rounded-xl" data-testid="diag-run-btn">
        {busy ? "Mengetes..." : "Run Diagnose"}
      </Button>

      {result && (
        <div className="border-t border-[#E5E5E5] pt-4 space-y-3" data-testid="diag-result">
          {/* Meta info */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            <div><b>Endpoint:</b> <code>{result.endpoint_used || "-"}</code></div>
            <div><b>HTTP:</b> <span className={result.http_status === 200 ? "text-emerald-700" : "text-red-700"}>{result.http_status}</span></div>
            <div><b>Rates:</b> {result.rates_count ?? 0}</div>
            <div><b>Provider:</b> <code>{result.provider_used}</code></div>
            <div><b>Degraded:</b> {result.degraded ? "Ya" : "Tidak"}</div>
            <div><b>Complete:</b> {result.complete_jubelio_rates ? "Ya" : "Tidak"}</div>
            <div><b>Coord Origin:</b> {result.request?.has_origin_coordinate ? "✓" : "✗"}</div>
            <div><b>Coord Dest:</b> {result.request?.has_destination_coordinate ? "✓" : "✗"}</div>
          </div>

          {/* Diagnosis message */}
          <div className={`p-3 rounded-lg border text-xs ${
            result.has_instant_couriers ? "bg-emerald-50 border-emerald-200 text-emerald-900"
              : result.has_instant_in_raw ? "bg-red-50 border-red-200 text-red-900"
              : "bg-amber-50 border-amber-200 text-amber-900"
          }`} data-testid="diag-message">
            {result.diagnosis_message}
          </div>

          {/* Instant couriers banner */}
          {result.instant_couriers?.length > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="diag-instant-found">
              <div className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-1">
                Instant Couriers Ditemukan ({result.instant_couriers.length})
              </div>
              <ul className="text-xs space-y-1">
                {result.instant_couriers.map((c, i) => (
                  <li key={i} className="font-mono" data-testid={`diag-instant-row-${i}`}>
                    <b>{c.courier_name}</b> · {c.courier_service_name} · Rp {Number(c.cost).toLocaleString("id-ID")} · provider={c.provider}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Couriers list */}
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#7A5440] mb-1">
              Semua Courier Names ({result.couriers_list?.length || 0})
            </div>
            <div className="flex flex-wrap gap-1" data-testid="diag-couriers-list">
              {(result.couriers_list || []).map((c, i) => (
                <span key={i} className="text-[10px] font-mono px-2 py-0.5 rounded bg-[#FFEDD5] text-[#E85D04] border border-[#FFD7A8]">
                  {c}
                </span>
              ))}
            </div>
          </div>

          {/* Filter input */}
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Filter Hasil (GoSend / Grab / Instant)</Label>
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="ketik 'gosend' / 'grab' / 'instant'"
              className="mt-1"
              data-testid="diag-filter-input"
            />
          </div>

          {/* Normalized rates (filtered) */}
          <details>
            <summary className="text-xs font-bold cursor-pointer text-[#5C5C5C] hover:text-[#1a1a1a]">
              Normalized Rates ({filtered.length}/{result.normalized_sample?.length || 0})
            </summary>
            <pre className="mt-2 text-[10px] bg-[#1a1a1a] text-[#e5e5e5] p-3 rounded overflow-x-auto max-h-72" data-testid="diag-normalized-json">
              {JSON.stringify(filtered, null, 2)}
            </pre>
          </details>

          {/* Raw response */}
          <details>
            <summary className="text-xs font-bold cursor-pointer text-[#5C5C5C] hover:text-[#1a1a1a]">
              Raw Response Sample (max 5)
            </summary>
            <pre className="mt-2 text-[10px] bg-[#1a1a1a] text-[#e5e5e5] p-3 rounded overflow-x-auto max-h-72" data-testid="diag-raw-json">
              {JSON.stringify(result.raw_response_sample, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </Card>
  );
}


// ─────────────── Phase 6: Webhook Receiver Settings ───────────────
function WebhookSettingsCard() {
  const [cfg, setCfg] = useState({
    webhook_url: "", header_name: "X-Jubelio-Webhook-Secret",
    secret_masked: "", _secret_set: false,
  });
  const [secret, setSecret] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [orphans, setOrphans] = useState([]);
  const [orphansLoading, setOrphansLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/admin/settings/jubelio-webhook");
      setCfg(r.data || {});
    } catch (e) { /* silent */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      if (secret && !secret.includes("***")) body.secret = secret;
      await api.put("/admin/settings/jubelio-webhook", body);
      toast.success("Secret webhook tersimpan");
      setSecret("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const generateRandomSecret = () => {
    const arr = new Uint8Array(24);
    window.crypto.getRandomValues(arr);
    const hex = Array.from(arr).map((b) => b.toString(16).padStart(2, "0")).join("");
    setSecret(`stwh_${hex}`);
    setShow(true);
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(cfg.webhook_url || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Webhook URL disalin");
    } catch { /* ignore */ }
  };

  const loadOrphans = async () => {
    setOrphansLoading(true);
    try {
      const r = await api.get("/admin/jubelio/webhook-events?limit=20");
      setOrphans(Array.isArray(r.data) ? r.data : []);
    } catch { setOrphans([]); }
    finally { setOrphansLoading(false); }
  };

  return (
    <Card className="p-6" data-testid="jubelio-webhook-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="font-display font-bold flex items-center gap-2">
          <ShieldCheck size={18} className="text-[#E85D04]" /> Webhook Penerimaan Status
        </h3>
        <span className="text-[10px] font-bold uppercase tracking-wider bg-[#FFEDD5] text-[#E85D04] px-2 py-1 rounded">Phase 6</span>
      </div>
      <p className="text-xs text-[#5C5C5C] mb-4">
        Daftarkan URL ini di Dashboard Jubelio Shipment / CS Jubelio supaya status pengiriman otomatis ter-update tanpa harus klik &quot;Refresh Tracking&quot;. Tetap aman: Cron sync (15 menit) &amp; Manual Refresh tetap berjalan sebagai fallback.
      </p>

      <div className="space-y-3">
        <div>
          <Label className="text-xs uppercase tracking-wider font-bold text-[#5C5C5C]">Webhook URL</Label>
          <div className="flex gap-2 mt-1">
            <Input
              value={cfg.webhook_url || ""}
              readOnly
              className="font-mono text-xs"
              data-testid="jubelio-webhook-url"
            />
            <Button
              type="button" variant="outline"
              onClick={copyUrl} disabled={!cfg.webhook_url}
              className="rounded-xl shrink-0"
              data-testid="jubelio-webhook-url-copy"
            >
              {copied ? "✓ Tersalin" : "Salin"}
            </Button>
          </div>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wider font-bold text-[#5C5C5C]">Header Name</Label>
          <Input value={cfg.header_name || "X-Jubelio-Webhook-Secret"} readOnly className="font-mono text-xs" />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <Label className="text-xs uppercase tracking-wider font-bold text-[#5C5C5C]">Secret (rahasia)</Label>
            <Button
              type="button" variant="link" size="sm"
              onClick={generateRandomSecret}
              className="text-xs text-[#E85D04] p-0 h-auto"
              data-testid="jubelio-webhook-generate-secret"
            >
              Generate Random
            </Button>
          </div>
          <div className="relative">
            <Input
              type={show ? "text" : "password"}
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder={cfg._secret_set ? cfg.secret_masked || "***" : "stwh_xxx..."}
              className="font-mono text-xs pr-10"
              data-testid="jubelio-webhook-secret-input"
            />
            <button
              type="button"
              onClick={() => setShow((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[#5C5C5C] hover:text-[#E85D04]"
              aria-label={show ? "Sembunyikan" : "Tampilkan"}
            >
              {show ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <div className="text-[10px] text-[#5C5C5C] mt-1">
            {cfg._secret_set ? (
              <>✓ Tersimpan ({cfg.secret_masked}). Biarkan kosong untuk mempertahankan secret yang sudah ada.</>
            ) : (
              <>Belum ada secret. Klik &quot;Generate Random&quot; atau isi manual. Lalu beri tahu CS Jubelio untuk set header webhook.</>
            )}
          </div>
        </div>

        <Button
          onClick={save}
          disabled={saving}
          className="brand-btn-primary rounded-xl font-bold"
          data-testid="jubelio-webhook-save"
        >
          <Save size={14} className="mr-1" /> {saving ? "Menyimpan..." : "Simpan Secret"}
        </Button>
      </div>

      <div className="mt-5 bg-[#FAF9F6] rounded-xl p-3 text-[11px] text-[#5C5C5C]">
        <div className="font-bold text-[#0E0E0E] mb-1">Cara setup di Jubelio:</div>
        <ol className="list-decimal ml-4 space-y-1">
          <li>Buka Dashboard Jubelio Shipment → Settings → Webhook (atau hubungi CS Jubelio).</li>
          <li>Set Notification URL = URL di atas.</li>
          <li>Tambah Header: <code className="bg-white px-1 rounded">X-Jubelio-Webhook-Secret</code> = secret di atas.</li>
          <li>Save. Test dengan generate AWB → status otomatis update.</li>
        </ol>
      </div>

      {/* Orphan events viewer */}
      <details className="mt-4">
        <summary className="cursor-pointer text-xs font-bold text-[#5C5C5C] hover:text-[#E85D04]" onClick={loadOrphans}>
          Orphan Webhook Events (debug)
        </summary>
        <div className="mt-2">
          {orphansLoading && <div className="text-xs text-[#5C5C5C]">Memuat...</div>}
          {!orphansLoading && orphans.length === 0 && (
            <div className="text-xs text-[#5C5C5C]">Tidak ada orphan event. Bagus!</div>
          )}
          <div className="space-y-1 max-h-64 overflow-auto">
            {orphans.map((ev, i) => (
              <div key={`orphan-${i}`} className="bg-[#FAF9F6] rounded p-2 text-[11px] font-mono">
                <div>{ev.received_at} · awb=<b>{ev.lookup_keys?.awb || "—"}</b> · status={ev.lookup_keys?.status || "—"}</div>
                <div className="text-[#5C5C5C]">reason: {ev.reason}</div>
              </div>
            ))}
          </div>
        </div>
      </details>
    </Card>
  );
}
